import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  InputAdornment,
  Divider,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
} from "@mui/material";
import { PortfolioApplyFund } from "../../util/API/portfolio";
import { commonRunQuery } from "../../util/API/loans";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 560 },
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: 4,
};

const ApplicationModal = ({ open, handleClose, product, userRole, user }) => {
  // Extract from session
  const userId = user?.User?.id || "";
  const userPin = user?.User?.pin || "";
  const firstName = user?.User?.firstName || "";
  const lastName = user?.User?.lastName || "";

  // Delegate state
  const [applicationMode, setApplicationMode] = useState("self");
  const [delegateEntities, setDelegateEntities] = useState([]);
  const [delegateLoading, setDelegateLoading] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  const [formData, setFormData] = useState({
    amount: "",
    investorName: userRole === "delegate" ? "" : `${firstName} ${lastName}`,
    fundingSource: "",
    agreedToTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Fetch delegate entities (same query as CreateProductForm)
  const getDelegateEntities = async () => {
    setDelegateLoading(true);
    try {
      const response = await commonRunQuery(370, userId);
      const record = response?.data?.body?.record;

      if (record) {
        const recordsArray = Array.isArray(record) ? record : [record];
        const mapped = recordsArray.map((item) => {
          const u = item.u;
          const uea = item.uea;
          return {
            id: u.investor_id,
            name: `${u.investor_firstName} ${u.investor_lastName}`,
            email: uea.investor_email,
          };
        });
        setDelegateEntities(mapped);
      }
    } catch (error) {
      console.error("getDelegateEntities error:", error);
    } finally {
      setDelegateLoading(false);
    }
  };

  const handleModeChange = (e, val) => {
    if (!val) return;
    setApplicationMode(val);
    setSelectedEntity(null);
    if (val === "delegate") getDelegateEntities();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Determine investor_id based on mode
    const investorId =
      applicationMode === "delegate" && selectedEntity
        ? selectedEntity.id
        : userId;

    // Validate delegate entity selection
    if (applicationMode === "delegate" && !selectedEntity) {
      setError("Please select a client / entity to apply on behalf of.");
      setLoading(false);
      return;
    }

    try {
      const result = await PortfolioApplyFund(
        userId,       
        userPin,       
        product?.id,   
        investorId,    
        formData.amount,
        "",
      );

      console.log("Apply Fund Response:", result);

      const callStatus =
        result?.payload?.callStatus ?? result?.data?.header?.callStatus;

      if (callStatus === 1 || callStatus === "1") {
        setSuccess(true);
      } else {
        setError(
          result?.payload?.msg || "Something went wrong. Please try again.",
        );
      }
    } catch (err) {
      console.error(err);
      setError("Request failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setFormData({
      amount: "",
      investorName: userRole === "delegate" ? "" : `${firstName} ${lastName}`,
      fundingSource: "",
      agreedToTerms: false,
    });
    setApplicationMode("self");
    setSelectedEntity(null);
    setDelegateEntities([]);
    setError("");
    setSuccess(false);
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleModalClose}>
      <Box sx={modalStyle}>
        <Typography variant="h5" fontWeight="900" color="#002e4c" gutterBottom>
          Investment Application
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Applying for: <strong>{product?.title}</strong>
        </Typography>

        {success ? (
          // Success State
          <Box textAlign="center" py={3}>
            <Typography
              variant="h6"
              color="success.main"
              fontWeight="bold"
              gutterBottom
            >
              🎉 Application Successful!
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              Your application for <strong>{product?.title}</strong> has been
              submitted and is pending admin review.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleModalClose}
              sx={{ bgcolor: "#002e4c", "&:hover": { bgcolor: "#004a7a" } }}
            >
              Close
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {error && <Alert severity="error">{error}</Alert>}

              {/* ── Delegate Toggle (visible to all users) ── */}
              <Box sx={{ p: 2, bgcolor: "#f0f4f8", borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  I am applying as:
                </Typography>
                <Stack spacing={2}>
                  <ToggleButtonGroup
                    value={applicationMode}
                    exclusive
                    onChange={handleModeChange}
                    size="small"
                    color="primary"
                  >
                    <ToggleButton value="self" sx={{ px: 3 }}>
                      Myself
                    </ToggleButton>
                    <ToggleButton value="delegate" sx={{ px: 3 }}>
                      On Behalf of Client
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {applicationMode === "delegate" && (
                    <Autocomplete
                      options={delegateEntities}
                      getOptionLabel={(option) =>
                        `${option.name} (${option.email})`
                      }
                      loading={delegateLoading}
                      value={selectedEntity}
                      onChange={(e, val) => setSelectedEntity(val)}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body2" fontWeight="600">
                              {option.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {option.email}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Client / Entity"
                          size="small"
                          required
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {delegateLoading ? (
                                  <CircularProgress color="inherit" size={16} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  )}
                </Stack>
              </Box>

              <TextField
                fullWidth
                label="Investment Amount"
                required
                type="number"
                placeholder={`Min: ${product?.minEntry}`}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
              />

              <Divider />

              <FormControlLabel
                control={
                  <Checkbox
                    required
                    checked={formData.agreedToTerms}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        agreedToTerms: e.target.checked,
                      })
                    }
                  />
                }
                label={
                  <Typography variant="caption">
                    I confirm that the information provided is accurate and I
                    have read the Offering Memorandum.
                  </Typography>
                }
              />

              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button fullWidth variant="outlined" onClick={handleModalClose}>
                  Cancel
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{ bgcolor: "#002e4c", "&:hover": { bgcolor: "#004a7a" } }}
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Submit Application"
                  )}
                </Button>
              </Box>
            </Stack>
          </form>
        )}
      </Box>
    </Modal>
  );
};

export default ApplicationModal;
